import React, { useState, useEffect, useRef, useCallback } from "react";
import { Brain, Send, ShieldCheck, Sparkles, Target, TrendingUp, Wallet } from "lucide-react";
import { Card, Button, Spinner, PageHeader } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { rp, pct, PeriodPicker } from "../../components/finance";

function inlineMd(s, k) {
  return String(s).split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={`${k}-${i}`} className="font-bold text-ink">{p.slice(2, -2)}</strong>
      : <span key={`${k}-${i}`}>{p}</span>);
}
function Markdown({ text }) {
  const lines = String(text || "").split("\n");
  const out = []; let list = [];
  const flush = (k) => { if (list.length) { out.push(<ul key={`ul-${k}`} className="my-1.5 ml-4 list-disc space-y-1">{list.map((l, i) => <li key={i}>{inlineMd(l, `li-${k}-${i}`)}</li>)}</ul>); list = []; } };
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, "");
    if (/^#{1,6}\s/.test(line)) { flush(idx); out.push(<p key={idx} className="mb-1 mt-2 font-heading text-sm font-bold text-ink">{inlineMd(line.replace(/^#{1,6}\s/, ""), `h-${idx}`)}</p>); }
    else if (/^\s*[-*]\s+/.test(line)) list.push(line.replace(/^\s*[-*]\s+/, ""));
    else if (/^\s*\d+[).]\s+/.test(line)) list.push(line.replace(/^\s*\d+[).]\s+/, ""));
    else if (line.trim() === "") { flush(idx); out.push(<div key={idx} className="h-1.5" />); }
    else { flush(idx); out.push(<p key={idx} className="my-0.5">{inlineMd(line, `p-${idx}`)}</p>); }
  });
  flush("end");
  return <div className="text-sm leading-relaxed text-ink">{out}</div>;
}

function ScenarioChips({ s, t }) {
  const i = s.impact;
  const chip = (icon, label, delta, sub) => {
    const Icon = icon; const good = delta >= 0;
    return (
      <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2">
        <Icon size={15} className="text-primary" />
        <div>
          <p className="text-[10px] uppercase tracking-wide text-ink-mute">{label}</p>
          <p className={`text-sm font-bold ${good ? "text-success-text" : "text-danger-text"}`}>{delta >= 0 ? "+" : ""}{rp(delta)}{sub ? ` · ${sub}` : ""}</p>
        </div>
      </div>
    );
  };
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="cfo-scenario-result">
      {chip(TrendingUp, t("Revenue Impact"), i.revenue.delta, i.revenue.delta_pct != null ? pct(i.revenue.delta_pct) : null)}
      {chip(Wallet, t("Profit Impact"), i.profit.delta)}
      {chip(Target, t("Break-even Revenue"), s.break_even.break_even_revenue, null)}
    </div>
  );
}

export default function AICFO() {
  const { t } = useT();
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [period, setPeriod] = useState("");
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => { api.get("/finance/cfo/suggestions").then(({ data }) => setSuggestions(data.groups || [])).catch(() => {}); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  const send = useCallback(async (question) => {
    const q = (question ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/finance/cfo/ask", { question: q, session_id: sessionRef.current, period: period || undefined });
      sessionRef.current = data.session_id;
      setMessages((m) => [...m, { role: "assistant", text: data.answer, scenario: data.scenario }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? t("The request took too long. Please try again.") : t("Something went wrong"));
      toast.error(msg);
      setMessages((m) => [...m, { role: "assistant", text: msg }]);
    } finally { setLoading(false); }
  }, [input, loading, period, t, toast]);

  return (
    <div data-testid="ai-cfo-page">
      <PageHeader title={t("AI CFO")} subtitle={t("Executive financial intelligence — read only")} icon={Brain}>
        <PeriodPicker value={period} onChange={setPeriod} />
      </PageHeader>

      <div className="mb-4 flex items-start gap-2.5 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="cfo-readonly-banner">
        <ShieldCheck size={18} className="mt-0.5 shrink-0 text-blue-600" />
        <p className="text-xs text-blue-800">{t("AI CFO only reads and analyzes. It never edits, approves, changes prices, or creates financial records.")}</p>
      </div>

      <Card className="mb-20 flex h-[calc(100vh-360px)] min-h-[440px] flex-col p-0 lg:mb-14" data-testid="cfo-chat-card">
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-5 sm:px-6" data-testid="cfo-messages">
          {messages.length === 0 && (
            <div className="mx-auto max-w-2xl py-2 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-light text-primary"><Brain size={26} /></div>
              <h3 className="font-heading text-lg font-bold text-ink">{t("Ask your AI CFO")}</h3>
              <p className="mt-1 text-sm text-ink-soft">{t("Revenue, profitability, cash flow, branch comparison, inventory, purchasing — and what-if scenarios, from your real data.")}</p>
              <div className="mt-5 space-y-3 text-left">
                {suggestions.map((g, gi) => (
                  <div key={gi}>
                    <p className="mb-1.5 text-[11px] font-extrabold uppercase tracking-wider text-ink-mute">{g.label}</p>
                    <div className="flex flex-wrap gap-2">
                      {g.questions.map((qq, qi) => (
                        <button key={qi} data-testid={`cfo-suggestion-${gi}-${qi}`} onClick={() => send(qq)}
                          className="rounded-full border border-line bg-white px-3 py-1.5 text-left text-xs font-medium text-ink-soft transition-colors hover:border-primary/40 hover:bg-primary-light hover:text-primary">{qq}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => m.role === "user" ? (
            <div key={i} className="flex justify-end" data-testid="cfo-message-user">
              <div className="max-w-[85%] rounded-2xl rounded-br-md bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm">{m.text}</div>
            </div>
          ) : (
            <div key={i} className="flex gap-2.5" data-testid="cfo-message-assistant">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary"><Sparkles size={16} /></div>
              <div className="max-w-[88%] rounded-2xl rounded-tl-md border border-line bg-white px-4 py-3 shadow-sm">
                <Markdown text={m.text} />
                {m.scenario && <ScenarioChips s={m.scenario} t={t} />}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-2.5" data-testid="cfo-loading">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary"><Sparkles size={16} /></div>
              <div className="flex items-center gap-2 rounded-2xl rounded-tl-md border border-line bg-white px-4 py-3 text-sm text-ink-soft shadow-sm"><Spinner /> {t("AI CFO is analyzing your finances…")}</div>
            </div>
          )}
        </div>

        <div className="border-t border-line bg-white p-3 sm:p-4">
          <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <textarea data-testid="cfo-chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder={t("Ask… e.g. Bagaimana jika harga ayam naik 15%?")} className="cf-input max-h-28 flex-1 resize-none" />
            <Button type="submit" loading={loading} disabled={!input.trim()} data-testid="cfo-send-button"><Send size={16} /> <span className="hidden sm:inline">{t("Send")}</span></Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
